import {
  SearchBar,
  SearchIcon,
  SingleSelectAutocomplete,
  SingleSelectAutocompleteOption,
  SingleSelectValue,
} from "@faro-lotv/flat-ui";
import { SearchBarProps } from "@faro-lotv/flat-ui/dist/components/search-bar/search-bar";
import { Box, Stack } from "@mui/system";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { FilterMenuRow, FilterMenuRowProps } from "./filter-menu-row";
import { FilterToggle } from "./filter-toggle";
import {
  AnnotationSortMenuButtonProps,
  SortMenuButton,
} from "./sort-menu-button";

type FilterAndSortPanel = Omit<FilterMenuRowProps, "isVisible"> &
  Partial<AnnotationSortMenuButtonProps> &
  Pick<SearchBarProps, "fullWidth" | "onTextChanged" | "text"> & {
    /** Optional tag name suggestions shown in the search autocomplete dropdown */
    suggestions?: string[];

    /**
     * Called when a suggestion is selected, with the selected text.
     * When provided, the search input renders an autocomplete with suggestions.
     */
    onSuggestionSelected?(suggestion: string): void;
  };

/**
 * @returns A UI component to define the search text, and filter and sorting options.
 * It is used both in the annotations panel and in the project tree view.
 */
export function SearchFilterSortPanel({
  onSortingChanged,
  hasFilters,
  children,
  onTextChanged,
  text,
  suggestions,
  onSuggestionSelected,
  ...props
}: PropsWithChildren<FilterAndSortPanel>): JSX.Element {
  const [isFilterRowVisible, toggleFilters] = useState(false);

  // Reveal the filter chips whenever a filter becomes active, so one applied elsewhere
  // (e.g. from the 2D overview's capture date control) is visible and removable here.
  useEffect(() => {
    if (hasFilters) toggleFilters(true);
  }, [hasFilters]);

  const resetSearch = useCallback(() => onTextChanged?.(""), [onTextChanged]);

  const { largeButtons, overlayRef } = useResponsiveLayout();

  return (
    <Box component="div" sx={{ mb: "4px" }} ref={overlayRef}>
      <Stack
        direction="row"
        spacing="6px"
        sx={{ justifyContent: "space-between" }}
      >
        <Box component="div" sx={{ flex: 1, minWidth: 0 }}>
          {onSuggestionSelected ? (
            <SearchBarWithSuggestions
              text={text}
              onTextChanged={onTextChanged}
              suggestions={suggestions ?? []}
              onSuggestionSelected={onSuggestionSelected}
            />
          ) : (
            <SearchBar
              placeholder="Search by name or tag"
              fullWidth
              onTextChanged={onTextChanged}
              text={text}
              onSearchReset={resetSearch}
              sx={{ height: "30px" }}
            />
          )}
        </Box>
        <FilterToggle
          isChecked={isFilterRowVisible}
          onToggle={toggleFilters}
          hasFilters={!!hasFilters}
          showButtonText={largeButtons}
        />
        {onSortingChanged && (
          <SortMenuButton
            onSortingChanged={onSortingChanged}
            showButtonText={largeButtons}
          />
        )}
      </Stack>

      {isFilterRowVisible && (
        <FilterMenuRow isVisible hasFilters={hasFilters} {...props}>
          {children}
        </FilterMenuRow>
      )}
    </Box>
  );
}

type SearchBarWithSuggestionsProps = {
  /** The current search text */
  text?: string;

  /** Called whenever the user types in the search field */
  onTextChanged?(text: string): void;

  /** The suggestions to show in the dropdown */
  suggestions: string[];

  /** Called when a suggestion is selected */
  onSuggestionSelected(suggestion: string): void;
};

/**
 * @returns A search bar with a tag-name autocomplete dropdown
 */
function SearchBarWithSuggestions({
  text,
  onTextChanged,
  suggestions,
  onSuggestionSelected,
}: SearchBarWithSuggestionsProps): JSX.Element {
  const options = useMemo<SingleSelectAutocompleteOption[]>(
    () =>
      suggestions.map((suggestion) => ({
        title: suggestion,
        value: suggestion,
      })),
    [suggestions],
  );

  const handleChange = useCallback(
    (value: SingleSelectValue) => {
      const name = typeof value === "string" ? value : value?.value;
      if (name) onSuggestionSelected(name);
    },
    [onSuggestionSelected],
  );

  return (
    <SingleSelectAutocomplete
      freeSolo
      options={options}
      inputValue={text ?? ""}
      onInputChange={onTextChanged}
      onChange={handleChange}
      // Options are already filtered upstream (prefix match), so keep them as-is.
      filterOptions={(currentOptions) => currentOptions}
      placeholder="Search by name or tag"
      startAdornment={<SearchIcon sx={{ width: "20px", height: "20px" }} />}
      hidePopupIcon
      showIcon={false}
      sx={{
        "& .MuiInputBase-root": {
          p: 0,
          pl: 1.75,
          // Make the input 30px high
          "& .MuiInputBase-input": { p: "5px 12px" },
        },
      }}
    />
  );
}

type ResponsiveLayout = {
  largeButtons: boolean;
  overlayRef: React.RefObject<HTMLDivElement | null>;
};

const MAX_WIDTH_BEFORE_LARGE_BUTTONS = 400;

/**
 * A hook that listens to the resize events of the containing box, changes the value of a layout output according to the new box size
 *
 * @returns An html reference to refer the containing box, and a boolean to inform whether it is possible to show large buttons in the box
 */
function useResponsiveLayout(): ResponsiveLayout {
  const [largeButtons, setLargeButtons] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!overlayRef.current) return;

    // Compute the initial layout
    setLargeButtons(
      overlayRef.current.offsetWidth > MAX_WIDTH_BEFORE_LARGE_BUTTONS,
    );

    const observer = new ResizeObserver((entries) => {
      if (!overlayRef.current) return;
      const entry = entries.find((e) => e.target === overlayRef.current);
      if (!entry) return;
      setLargeButtons(
        overlayRef.current.offsetWidth > MAX_WIDTH_BEFORE_LARGE_BUTTONS,
      );
    });

    // Attach the resize observer to the stack
    observer.observe(overlayRef.current);
    return () => {
      observer.disconnect();
    };
  }, []);

  return { largeButtons, overlayRef };
}
